import React, { useContext, useEffect } from "react";
import styled, { useTheme } from "styled-components";
import { secured } from "react-abac";
import { AuthoritiesEnum, CRUDActionEnum, RoutesEnum, StatusStateEnum } from "~/types/enums";
import RestrictedSection from "~/components/RestrictedSection";
import { Avatar, Button, Card, Dropdown, Grid, Icon, Link, Modal, Picture, Section, Spinner, Text } from "~/components-shared";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import CoreContext from "~/lib/context";
import requestSelectors from "~/store/requests/selectors";
import requestsActionCreators from "~/store/requests/actions";
import { useHistory, useLocation, useParams } from "react-router-dom";
import RequestUpdateModal from "~/components/RequestUpdateModal";
import RequestDeleteModal from "~/components/RequestDeleteModal";
import authSelectors from "~/store/auth/selectors";
import ErrorSection from "~/components/ErrorSection";
import useAbacExt from "~/hooks/abac";
import Markdown from "~/components/Markdown";
import moment from "moment";
import RequestActionsCard from "~/components/RequestActionsCard";
import EventCreateModal from "~/components/EventCreateModal";
import RequestCloseModal from "~/components/RequestCloseModal";
import RequestCommentCard from "~/components/RequestCommentsCard";
import RequestCommentDeleteModal from "~/components/RequestCommentDeleteModal";
import { useHashFragment } from "~/hooks/common";
import requestCommentSelectors from "~/store/requestComments/selectors";
import eventSelectors from "~/store/events/selectors";
import DefaultSideBar from "~/components/DefaultSideBar";
const StyledDropdown = styled(Dropdown)`
  display: inline-block;
  vertical-align: bottom;
  margin-left: ${_ref => {
  let {
    theme
  } = _ref;
  return theme.spacing.step * 3;
}}px;
`;
const Request = () => {
  const {
    colors
  } = useTheme();
  const {
    t
  } = useTranslation();
  const dispatch = useDispatch();
  const {
    setSidebar
  } = useContext(CoreContext);
  const user = useSelector(authSelectors.getUser);
  const {
    requestId
  } = useParams();
  const status = useSelector(requestSelectors.getStatusByRequest(requestId));
  const action = useSelector(requestSelectors.getAction);
  const commentAction = useSelector(requestCommentSelectors.getAction);
  const eventAction = useSelector(eventSelectors.getAction);
  const error = useSelector(requestSelectors.getErrorByRequest(requestId));
  const request = useSelector(requestSelectors.getRequest(requestId));
  const history = useHistory();
  const location = useLocation();
  const {
    userHas,
    userHasOneOf
  } = useAbacExt();
  useHashFragment();
  useEffect(() => {
    setSidebar(/*#__PURE__*/React.createElement(DefaultSideBar, null));
  }, [setSidebar]);
  useEffect(() => {
    if (status === StatusStateEnum.IDLE && !request) {
      dispatch(requestsActionCreators.fetchOneStarted(requestId));
    }
  }, [dispatch, requestId, status]);
  if (!request && status === StatusStateEnum.FAILED) {
    return /*#__PURE__*/React.createElement(ErrorSection, {
      error: error
    });
  }
  if (!request) {
    return /*#__PURE__*/React.createElement(Section, null, /*#__PURE__*/React.createElement(Spinner, {
      center: true
    }));
  }
  const handleViewMember = id => {
    history.push(RoutesEnum.MEMBER.replace(":memberId", id));
  };
  const handleEdit = () => {
    dispatch(requestsActionCreators.actionChanged(CRUDActionEnum.UPDATE, request.id));
  };
  const handleDelete = () => {
    dispatch(requestsActionCreators.actionChanged(CRUDActionEnum.DELETE, request.id));
  };
  const handleCommunityClick = () => {
    history.push(RoutesEnum.COMMUNITY.replace(":communityId", request.community.id));
  };
  const handleAuthorClick = () => {
    history.push(RoutesEnum.MEMBER.replace(":memberId", request.createdBy.id));
  };
  const handleVolunteer = () => {
    dispatch(requestsActionCreators.volunteered(request.id));
  };
  const handleRevoke = () => {
    dispatch(requestsActionCreators.revoked(request.id));
  };
  const handleLike = () => {
    dispatch(requestsActionCreators.liked(request.id));
  };
  const handleUnlike = () => {
    dispatch(requestsActionCreators.unliked(request.id));
  };
  const handleCommentsClick = () => {
    window.history.pushState(undefined, "Title", `${location.pathname}#comments`);
  };
  const volunteersReached = request.capVolunteers && request.volunteers.length >= request.volunteerNumber;
  const dateExpired = moment(request.date).valueOf() <= moment().valueOf();
  return /*#__PURE__*/React.createElement(Section, null, /*#__PURE__*/React.createElement(Grid, {
    cols: 1,
    s: {
      mb: 3
    }
  }, /*#__PURE__*/React.createElement(Grid.Cell, {
    justify: "end"
  }, request.closedAt ? /*#__PURE__*/React.createElement(Button, {
    color: "info",
    s: {
      ml: 3,
      px: 5,
      py: 2
    },
    disabled: true,
    rounded: true,
    display: "inline-block"
  }, t("Request.closed")) : !volunteersReached && !dateExpired ? request.volunteers.find(v => v.id === user?.id) ? /*#__PURE__*/React.createElement(Button, {
    onClick: handleRevoke,
    color: "dangerGrey",
    s: {
      ml: 3,
      px: 5,
      py: 2
    },
    rounded: true,
    display: "inline-block"
  }, t("common.actions.revoke")) : /*#__PURE__*/React.createElement(Button, {
    onClick: handleVolunteer,
    color: "secondary",
    s: {
      ml: 3,
      px: 5,
      py: 2
    },
    rounded: true,
    display: "inline-block"
  }, t("common.actions.volunteer")) : /*#__PURE__*/React.createElement(React.Fragment, null, volunteersReached && /*#__PURE__*/React.createElement(Button, {
    color: "info",
    s: {
      ml: 3,
      px: 5,
      py: 2
    },
    disabled: true,
    rounded: true,
    display: "inline-block"
  }, t("Request.amountReached")), dateExpired && /*#__PURE__*/React.createElement(Button, {
    color: "info",
    s: {
      ml: 3,
      px: 5,
      py: 2
    },
    disabled: true,
    rounded: true,
    display: "inline-block"
  }, t("Request.dateExceeded"))), !request.closedAt && userHasOneOf([AuthoritiesEnum.REQUESTS_WRITE], request.createdBy.id) && /*#__PURE__*/React.createElement(StyledDropdown, {
    direction: "bottomLeft",
    handlerProps: {
      rounded: true,
      s: {
        p: 2
      },
      "data-tooltip-id": "general-tooltip",
      "data-tooltip-content": t("common.actions.options")
    },
    handler: /*#__PURE__*/React.createElement(Icon, {
      scale: 5,
      src: "dots-vertical",
      color: colors.text.primary,
      display: "block"
    })
  }, _ref2 => {
    let {
      isOpen,
      onItemClick
    } = _ref2;
    return /*#__PURE__*/React.createElement(Dropdown.List, {
      isOpen: isOpen
    }, userHasOneOf([AuthoritiesEnum.REQUESTS_WRITE], request?.createdBy.id) && /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      onClick: () => {
        handleEdit();
        onItemClick();
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      scale: 4.5,
      src: "pencil",
      s: {
        mr: 2
      },
      color: "primary"
    }), t("common.actions.edit")), userHasOneOf([AuthoritiesEnum.REQUESTS_WRITE], request?.createdBy.id) && /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      onClick: () => {
        handleDelete();
        onItemClick();
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      scale: 4.5,
      src: "bin",
      s: {
        mr: 2
      },
      color: "danger"
    }), t("common.actions.delete")));
  }))), /*#__PURE__*/React.createElement(Grid, {
    cols: 2,
    gap: 7,
    template: "120px auto"
  }, /*#__PURE__*/React.createElement(Picture, {
    src: request.community.picture,
    name: request.community.name,
    color: request.community.color,
    s: {
      mb: 7
    }
  }), /*#__PURE__*/React.createElement(Grid.Cell, {
    justify: "start"
  }, /*#__PURE__*/React.createElement(Text, {
    color: "textSecondary",
    fontSize: 4
  }, t("Request.sharedBy"), " ", /*#__PURE__*/React.createElement(Link, {
    onClick: handleAuthorClick,
    weight: "semiBold",
    color: colors.text.primary
  }, request.createdBy.firstName, " ", request.createdBy.lastName), " ", t("Request.in"), " ", /*#__PURE__*/React.createElement(Link, {
    onClick: handleCommunityClick,
    weight: "semiBold",
    fontSize: 4,
    color: colors.text.primary
  }, "@", request.community.name)), /*#__PURE__*/React.createElement(Text, {
    element: "h1",
    s: {
      mb: 3,
      mt: 1
    }
  }, request.name))), request.description ? /*#__PURE__*/React.createElement(Text, {
    element: "p",
    s: {
      mb: 2
    },
    fontSize: 4,
    lineHeight: 7
  }, /*#__PURE__*/React.createElement(Markdown, {
    text: request.description
  })) : /*#__PURE__*/React.createElement(Text, {
    s: {
      mb: 2
    },
    display: "block",
    color: "textSecondary"
  }, t("Request.noDescription")), /*#__PURE__*/React.createElement(Text, {
    element: "span",
    display: "inline-block"
  }, request.likes.find(v => v.id === user?.id) ? /*#__PURE__*/React.createElement(Button, {
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-content": t("Request.likes"),
    onClick: handleUnlike,
    color: "transparent",
    s: {
      px: 3,
      py: 1,
      ml: -3
    }
  }, /*#__PURE__*/React.createElement(Icon, {
    color: colors.danger.main,
    src: "heart"
  }), " ", request.likes.length) : /*#__PURE__*/React.createElement(Button, {
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-content": t("Request.likes"),
    onClick: handleLike,
    color: "transparent",
    s: {
      px: 3,
      py: 1,
      ml: -3
    }
  }, /*#__PURE__*/React.createElement(Icon, {
    color: colors.secondary.main,
    src: "heart"
  }), " ", request.likes.length)), /*#__PURE__*/React.createElement(Button, {
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-content": t("Request.comments"),
    onClick: handleCommentsClick,
    display: "inline-block",
    color: "transparent",
    s: {
      px: 3,
      py: 1
    }
  }, /*#__PURE__*/React.createElement(Icon, {
    color: colors.secondary.main,
    src: "message"
  }), " ", request.commentCount), userHasOneOf([AuthoritiesEnum.REQUESTS_WRITE], request.createdBy.id) && /*#__PURE__*/React.createElement(RequestActionsCard, {
    request: request,
    reached: volunteersReached,
    dateExpired: dateExpired,
    s: {
      my: 6,
      p: 5
    }
  }), /*#__PURE__*/React.createElement(Grid, {
    cols: 2,
    s: {
      mt: 5,
      mb: 5
    }
  }, /*#__PURE__*/React.createElement(Text, {
    element: "h3"
  }, t("Request.volunteers"), /*#__PURE__*/React.createElement(Text, {
    fontSize: 4,
    element: "span",
    color: "textSecondary"
  }, " – ", t("Request.needed", {
    context: request.capVolunteers ? "cap" : "",
    number: request.volunteerNumber,
    date: moment(request.date).format("ddd, MMMM D hh:mma")
  }))), /*#__PURE__*/React.createElement(Grid.Cell, {
    justify: "end"
  }, /*#__PURE__*/React.createElement(Text, {
    element: "span",
    color: "textSecondary"
  }, /*#__PURE__*/React.createElement(Icon, {
    src: "people",
    color: colors.text.secondary,
    scale: 3
  }), " ", request.volunteers.length, " ", /*#__PURE__*/React.createElement(Text, {
    element: "span",
    color: colors.secondary.main
  }, "(", request.volunteers.filter(attendee => attendee.active).length, ")")))), request.volunteers.length ? /*#__PURE__*/React.createElement(Grid, {
    cols: 2,
    equal: true,
    s: {
      mb: 5
    },
    gap: 3,
    align: "stretch"
  }, request.volunteers.map(volunteer => /*#__PURE__*/React.createElement(Card, {
    key: volunteer.id,
    onClick: () => handleViewMember(volunteer.id),
    s: {
      p: 3
    },
    border: true
  }, /*#__PURE__*/React.createElement(Grid, {
    cols: 3,
    template: "4rem 1fr 20px",
    gap: 5,
    align: "center"
  }, /*#__PURE__*/React.createElement(Avatar, {
    user: volunteer,
    size: 14
  }), /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
    weight: "semiBold",
    color: "textSecondary",
    display: "block",
    fontSize: 3,
    lineHeight: 4
  }, volunteer.role.name), /*#__PURE__*/React.createElement(Text, {
    weight: "semiBold",
    display: "block",
    fontSize: 4,
    lineHeight: 5,
    s: {
      my: 1
    }
  }, volunteer.firstName, " ", volunteer.lastName), /*#__PURE__*/React.createElement(Text, {
    color: "textSecondary",
    display: "block",
    lineHeight: 4
  }, volunteer.email)), /*#__PURE__*/React.createElement(Icon, {
    src: "chevron",
    rotate: 90,
    color: colors.secondary.main
  }))))) : /*#__PURE__*/React.createElement(Text, {
    color: "textSecondary",
    fontSize: 4,
    weight: "semiBold",
    display: "block",
    s: {
      mb: 5
    }
  }, t("Request.noVolunteers")), userHas(AuthoritiesEnum.REQUEST_COMMENTS_READ) && /*#__PURE__*/React.createElement(RequestCommentCard, {
    id: "comments",
    requestId: requestId,
    s: {
      p: 0
    },
    title: /*#__PURE__*/React.createElement(Text, {
      element: "h3"
    }, t("Request.comments"))
  }), userHasOneOf([AuthoritiesEnum.REQUESTS_WRITE], request.createdBy.id) && /*#__PURE__*/React.createElement(Modal, {
    open: action === CRUDActionEnum.UPDATE
  }, /*#__PURE__*/React.createElement(RequestUpdateModal, null)), userHasOneOf([AuthoritiesEnum.REQUESTS_WRITE], request.createdBy.id) && /*#__PURE__*/React.createElement(Modal, {
    open: action === CRUDActionEnum.DELETE
  }, /*#__PURE__*/React.createElement(RequestDeleteModal, null)), userHasOneOf([AuthoritiesEnum.REQUEST_COMMENTS_WRITE]) && /*#__PURE__*/React.createElement(Modal, {
    open: commentAction === CRUDActionEnum.DELETE
  }, /*#__PURE__*/React.createElement(RequestCommentDeleteModal, null)), userHasOneOf([AuthoritiesEnum.REQUESTS_WRITE], request.createdBy.id) && /*#__PURE__*/React.createElement(Modal, {
    open: action === CRUDActionEnum.CLOSE
  }, /*#__PURE__*/React.createElement(RequestCloseModal, null)), userHas(AuthoritiesEnum.EVENTS_WRITE) && /*#__PURE__*/React.createElement(Modal, {
    open: eventAction === CRUDActionEnum.CREATE
  }, /*#__PURE__*/React.createElement(EventCreateModal, null)));
};
export default /*#__PURE__*/React.memo(secured({
  permissions: AuthoritiesEnum.REQUESTS_READ,
  noAccess: () => /*#__PURE__*/React.createElement(RestrictedSection, null)
})(Request), () => true);